/* eslint-disable @typescript-eslint/no-explicit-any */
import momentTimeZone, { Moment } from 'moment-timezone';
import i18nDateTimeFormatter from './i18n/i18n-datetime-formatter';

/** Moment pattern for replay wall-clock strings (subscription TZ via wrapper; 12h + AM/PM). */
const replayUserWallClock = 'h:mm:ss A';

export const DATE_FORMAT = {
    iso: 'YYYY-MM-DD', // 2020-04-04
    short: 'MMM D, YYYY', // Nov 4, 2022,
    shortNoYear: 'MMM D', // Nov 4,
    shortNoDay: 'MMM YYYY', // Nov 2022,
    full: 'MMM D, YYYY h:mm:ss A z',
    dateTimeGenericZone: 'MMM D, YYYY h:mm A z', // no seconds; generic zone abbreviation (ET/PT) for space-constrained surfaces
    fullDate: 'MMMM D, YYYY',
    time: 'h:mma',
    isoDateTime: 'YYYY-MM-DD hh:mm:ss',
    isoDateTime24: 'YYYY-MM-DD HH:mm:ss',
    longIsoNoYear: 'MMMM DD', // November 04
    replayUserTimeFull: replayUserWallClock,
    replayUserTimeEventLog: replayUserWallClock,
};

let currentTimezone: string;

export function setCurrentTimezoneForAppWideMoment (newTz: string) {
    currentTimezone = newTz;
}

export function getCurrentTimezone () {
    return currentTimezone;
}

// Function to format using i18nDateTimeFormatter
// which is monkey patched to the moment object
function formatWithI18n (
    moment: Moment,
    originalFormat: (formatStr: string) => string,
    formatStr: string,
    ignoreTimeZone = false,
) {
    // If the datetime string passed into moment is invalid,
    // preserve Moment's error handling
    // by using the original format function
    if (!moment.isValid()) {
        return originalFormat.call(moment, formatStr);
    }

    switch (formatStr) {
        case DATE_FORMAT.full:
            return i18nDateTimeFormatter.formatFull({ date: moment.toDate(), ignoreTimeZone });
        case DATE_FORMAT.short:
            return i18nDateTimeFormatter.formatShort({ date: moment.toDate(), ignoreTimeZone });
        case DATE_FORMAT.dateTimeGenericZone:
            return i18nDateTimeFormatter.formatShortDateTimeGenericZone({ date: moment.toDate(), ignoreTimeZone });
        default:
            return originalFormat.call(moment, formatStr);
    }
}

/**
 * Call this function with the same parameters as standard Moment.
 * Optionally, include a plain object with customization options as the final argument:
 *
 * - ignoreTimeZone: Interpret the given date in the browser's local time zone, rather than the subscription time zone.
 *                   This parameter essentially disables all of the customization that this wrapper provides.
 *
 * For example, if the subscription timezone is Asia/Taipei and local timezone is America/Toronto:
 *
 *     var b = MomentWrapper("2013-11-18 11:55");                            // November 19, 00:55 in Taipei (Nov 18, 11:55 Toronto time converted to Taipei)
 *     var c = MomentWrapper("2013-11-18 11:55", { ignoreTimeZone: true });  // November 18, 11:55 in Toronto
 *
 * Moments b and c have the same Unix timestamp associated with them, but a different date string.
 */
export default function (...args: any[]): any {
    const customArgs = args[args.length - 1]; // take the last argument so that the rest of moment works as normal
    if (args.length > 1 && customArgs && customArgs.ignoreTimeZone) {
        args.splice(args.length - 1);
    }

    let timeZone: string | undefined;
    if (!customArgs?.ignoreTimeZone) {
        timeZone = getCurrentTimezone();
    }

    let moment: Moment;
    if (timeZone) {
        moment = momentTimeZone.tz(args[0], timeZone);
    } else {
        moment = momentTimeZone(...args);
    }
    const originalFormat = moment.format;
    moment.format = (str: string) => formatWithI18n(moment, originalFormat, str, Boolean(customArgs?.ignoreTimeZone));

    return moment;
}
